import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import PageLoader from "../components/globals/PageLoader";

type PageLoaderContextType = {
  withPageLoader: <T>(task: () => Promise<T>) => Promise<T>;
};

type PageLoaderProviderProps = {
  children: React.ReactNode;
};

const PageLoaderContext = createContext<PageLoaderContextType | null>(null);

export const PageLoaderProvider: React.FC<PageLoaderProviderProps> = ({ children }) => {
  const [openCount, setOpenCount] = useState(0);

  const showPageLoader = useCallback(() => {
    setOpenCount((count) => count + 1);
  }, []);

  const hidePageLoader = useCallback(() => {
    setOpenCount((count) => Math.max(0, count - 1));
  }, []);

  const withPageLoader = useCallback(async <T,>(task: () => Promise<T>): Promise<T> => {
    showPageLoader();
    try {
      return await task();
    } finally {
      hidePageLoader();
    }
  }, [hidePageLoader, showPageLoader]);

  const value = useMemo(() => ({ withPageLoader }), [withPageLoader]);

  return (
    <PageLoaderContext.Provider value={value}>
      <PageLoader open={openCount > 0} />
      {children}
    </PageLoaderContext.Provider>
  );
};

export const usePageLoader = () => {
  const context = useContext(PageLoaderContext);
  if (!context) {
    throw new Error("usePageLoader must be used within a PageLoaderProvider");
  }
  return context;
};
